import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { GUEST_TOKEN_PATTERN, getGuestOrderToken, guestOrderLink, saveGuestOrderToken } from "@/lib/guestOrderAccess";
import { CheckCircle2, Clock, Copy, Package, XCircle, AlertTriangle, ArrowLeft, Loader2, RotateCcw } from "lucide-react";

interface OrderView {
  id: string;
  reference: string;
  status: string;
  created_at: string;
  total: number;
  currency: string;
  store_id: string | null;
  store_name: string | null;
  shipping_carrier: string | null;
  tracking_number: string | null;
  is_guest: boolean;
  can_resume?: boolean;
  payment_declined?: boolean;
  reservation_expires_at?: string | null;
  reservation_expired?: boolean;
  customer_email: string | null;
  items: { name: string; quantity: number; unit_price: number; image: string | null }[];
}

const STATUS: Record<string, { label: string; tone: "ok" | "wait" | "bad" | "warn" }> = {
  pending: { label: "En attente de paiement", tone: "wait" },
  processing: { label: "Payée — en préparation", tone: "ok" },
  completed: { label: "Terminée", tone: "ok" },
  cancelled: { label: "Annulée", tone: "bad" },
  refunded: { label: "Remboursée", tone: "warn" },
  payment_failed: { label: "Paiement refusé", tone: "bad" },
  payment_review: { label: "Paiement en cours de vérification", tone: "warn" },
};

const EXPIRED_MESSAGE = "Votre session de paiement a expiré et les articles ont été remis en vente.";
const DECLINED_MESSAGE = "Votre carte a été refusée. Essayez une autre carte ou contactez votre banque.";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Old Stripe return links (/orders/:id) land on the tracking page. */
export function LegacyOrderRedirect() {
  const { orderId = "" } = useParams();
  const { search } = useLocation();
  return <Navigate to={`/commande/${orderId}${search}`} replace />;
}

export default function OrderTracking() {
  const { orderId = "" } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const paymentReturn = params.get("payment") === "success";
  const interrupted = params.get("paiement") === "interrompu";

  // Guest token: taken from the link once, kept in this browser, removed from the address bar.
  const urlToken = params.get("t");
  const [token] = useState<string | null>(() => {
    if (urlToken && GUEST_TOKEN_PATTERN.test(urlToken) && UUID.test(orderId)) {
      saveGuestOrderToken(orderId, urlToken);
      return urlToken;
    }
    return UUID.test(orderId) ? getGuestOrderToken(orderId) : null;
  });
  useEffect(() => {
    if (urlToken) navigate(`/commande/${orderId}${paymentReturn ? "?payment=success" : interrupted ? "?paiement=interrompu" : ""}`, { replace: true });
  }, [urlToken, orderId, paymentReturn, interrupted, navigate]);

  const [order, setOrder] = useState<OrderView | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");

  const load = useCallback(async () => {
    if (!UUID.test(orderId)) { setState("notfound"); return null; }
    const { data, error } = await supabase.functions.invoke("get-order-status", {
      body: { order_id: orderId, ...(token ? { guest_token: token } : {}) },
    });
    if (error) {
      let code = "";
      try { code = (await (error as any)?.context?.json?.())?.code ?? ""; } catch { /* ignore */ }
      setState(code === "ORDER_NOT_FOUND" ? "notfound" : "error");
      return null;
    }
    setOrder(data.order);
    setState("ready");
    return data.order as OrderView;
  }, [orderId, token]);

  // After a Stripe return the webhook may lag a few seconds: poll while still pending.
  useEffect(() => {
    let tries = 0; let timer: ReturnType<typeof setTimeout> | undefined; let stop = false;
    const tick = async () => {
      const o = await load();
      if (!stop && paymentReturn && o?.status === "pending" && ++tries < 10) timer = setTimeout(tick, 3000);
    };
    tick();
    return () => { stop = true; if (timer) clearTimeout(timer); };
  }, [load, paymentReturn]);

  const money = useMemo(() => {
    const cur = order?.currency ?? "EUR";
    try { return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }); }
    catch { return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }); }
  }, [order?.currency]);

  // LOT 2: resume the SAME order (the server reuses its open session, or reports it expired/paid).
  const [resuming, setResuming] = useState(false);
  const [resumeNotice, setResumeNotice] = useState<string | null>(null);
  const resume = async () => {
    setResuming(true); setResumeNotice(null);
    try {
      const { data, error } = await supabase.functions.invoke("create-order-checkout", {
        body: { order_id: orderId, ...(token ? { guest_token: token } : {}) },
      });
      if (!error && data?.url) { window.location.href = data.url; return; }
      let code = "";
      try { code = (await (error as any)?.context?.json?.())?.code ?? ""; } catch { /* ignore */ }
      setResumeNotice(code === "RESERVATION_EXPIRED" ? EXPIRED_MESSAGE
        : code === "ORDER_ALREADY_PAID" ? "Cette commande est déjà payée."
        : "La reprise du paiement n'a pas pu aboutir. Veuillez réessayer.");
      await load();
    } finally { setResuming(false); }
  };

  const copyLink = async () => {
    if (!token) return;
    try {
      await navigator.clipboard.writeText(guestOrderLink(window.location.origin, orderId, token));
      toast({ title: "Lien copié", description: "Conservez-le pour suivre votre commande." });
    } catch {
      toast({ title: "Copie impossible", description: "Copiez le lien depuis la barre d'adresse.", variant: "destructive" });
    }
  };

  if (state === "loading") {
    return (
      <main className="min-h-screen bg-background px-4 py-16">
        <div className="mx-auto max-w-2xl space-y-4">
          <Skeleton className="h-10 w-2/3" />
          <Skeleton className="h-64 w-full" />
        </div>
      </main>
    );
  }

  if (state !== "ready" || !order) {
    return (
      <main className="min-h-screen bg-background px-4 py-24">
        <div className="mx-auto max-w-md text-center space-y-4">
          <AlertTriangle className="mx-auto h-10 w-10 text-muted-foreground" />
          <h1 className="text-2xl font-semibold text-foreground">
            {state === "notfound" ? "Commande introuvable" : "Suivi momentanément indisponible"}
          </h1>
          <p className="text-muted-foreground">
            {state === "notfound"
              ? "Ce lien de suivi est invalide, ou il a été ouvert depuis un autre navigateur. Utilisez le lien complet reçu après votre paiement, ou connectez-vous au compte utilisé pour la commande."
              : "Veuillez réessayer dans un instant."}
          </p>
          <Button asChild variant="outline"><Link to="/">Retour à l'accueil</Link></Button>
        </div>
      </main>
    );
  }

  const expired = !!order.reservation_expired;
  const s = expired ? { label: "Session expirée", tone: "bad" as const } : STATUS[order.status] ?? { label: "Mise à jour en cours", tone: "wait" as const };
  const until = order.reservation_expires_at ? new Date(order.reservation_expires_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }) : null;
  const confirming = paymentReturn && order.status === "pending";
  const Icon = s.tone === "ok" ? CheckCircle2 : s.tone === "bad" ? XCircle : s.tone === "warn" ? AlertTriangle : Clock;

  return (
    <main className="min-h-screen bg-background px-4 py-16 md:py-24">
      <div className="mx-auto max-w-2xl space-y-8">
        <header className="space-y-3 text-center">
          <Icon className={`mx-auto h-12 w-12 ${s.tone === "ok" ? "text-primary" : "text-muted-foreground"}`} />
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {order.status === "processing" || order.status === "completed" ? "Merci pour votre commande" : confirming ? "Confirmation du paiement…" : `Commande #${order.reference}`}
          </h1>
          <p className="text-muted-foreground">
            {confirming
              ? "Nous attendons la confirmation de votre paiement. Cette page se met à jour automatiquement."
              : <>Commande <span className="font-medium text-foreground">#{order.reference}</span>{order.store_name ? <> chez <span translate="no">{order.store_name}</span></> : null}</>}
          </p>
        </header>

        {(expired || (order.status === "pending" && !confirming) || resumeNotice) && (
          <Card className={expired || order.payment_declined ? "border-destructive" : "border-primary"} role="status">
            <CardContent className="space-y-4 pt-6">
              {expired ? (
                <p className="font-medium text-foreground">{EXPIRED_MESSAGE}</p>
              ) : order.can_resume ? (
                <>
                  <div className="space-y-1">
                    <p className="font-medium text-foreground">{order.payment_declined ? DECLINED_MESSAGE : interrupted ? "Paiement interrompu" : "Paiement en attente"}</p>
                    <p className="text-sm text-muted-foreground">
                      Vos articles restent réservés{until ? <> jusqu'à <span className="font-medium text-foreground">{until}</span></> : null}. Reprenez le paiement de cette même commande : aucun nouvel achat ne sera créé.
                    </p>
                  </div>
                  <Button onClick={resume} disabled={resuming} className="w-full sm:w-auto">
                    {resuming ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                    Reprendre le paiement
                  </Button>
                </>
              ) : null}
              {resumeNotice && <p className="text-sm text-foreground">{resumeNotice}</p>}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0">
            <CardTitle className="flex items-center gap-2 text-lg"><Package className="h-5 w-5" />Récapitulatif</CardTitle>
            <Badge variant={s.tone === "bad" ? "destructive" : s.tone === "ok" ? "default" : "secondary"}>{confirming ? "Confirmation en cours" : s.label}</Badge>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="divide-y divide-border">
              {order.items.map((it, i) => (
                <li key={i} className="flex items-center justify-between gap-4 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {it.image ? <img src={it.image} alt="" className="h-12 w-12 rounded-md object-cover" loading="lazy" /> : <div className="h-12 w-12 rounded-md bg-muted" />}
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground" translate="no">{it.name}</p>
                      <p className="text-sm text-muted-foreground">Quantité : {it.quantity}</p>
                    </div>
                  </div>
                  <span className="font-medium text-foreground">{money.format(it.unit_price * it.quantity)}</span>
                </li>
              ))}
            </ul>
            <div className="flex justify-between border-t border-border pt-4 text-lg font-semibold text-foreground">
              <span>{order.status === "processing" || order.status === "completed" ? "Total payé" : "Total"}</span><span>{money.format(order.total)}</span>
            </div>
            {order.shipping_carrier && <p className="text-sm text-muted-foreground">Livraison : {order.shipping_carrier}{order.tracking_number ? ` — suivi ${order.tracking_number}` : ""}</p>}
            <p className="text-sm text-muted-foreground">Passée le {new Date(order.created_at).toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short" })}</p>
          </CardContent>
        </Card>

        {order.is_guest && token && (
          <Card>
            <CardContent className="flex flex-col gap-3 pt-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-medium text-foreground">Conservez votre lien de suivi</p>
                <p className="text-sm text-muted-foreground">
                  Ce lien personnel est le seul moyen de retrouver cette commande sans compte{order.customer_email ? ` (commande au nom de ${order.customer_email})` : ""}. Ne le partagez pas.
                </p>
              </div>
              <Button variant="outline" onClick={copyLink} className="shrink-0"><Copy className="mr-2 h-4 w-4" />Copier le lien</Button>
            </CardContent>
          </Card>
        )}

        {order.store_id && (
          <div className="text-center">
            <Button asChild variant="ghost"><Link to={`/boutique-apercu/${order.store_id}`}><ArrowLeft className="mr-2 h-4 w-4" />Retour à la boutique</Link></Button>
          </div>
        )}
      </div>
    </main>
  );
}
